import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../api/http';
import { t } from '../../i18n';

interface MyClass {
  schoolId: string;
  schoolName: string;
  academicYear: string;
  sectionId: string;
  sectionName: string;
  className: string;
  isClassTeacher: boolean;
  subjects: string[];
  studentCount: number;
}

/** Teacher home: the sections the signed-in teacher is responsible for this year. */
export function MyClassesPage() {
  const mine = useQuery({
    queryKey: ['me', 'classes'],
    queryFn: () => api<MyClass[]>('/me/classes'),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold">{t('myclasses.title')}</h1>
      {mine.isPending && <p role="status">{t('common.loading')}</p>}
      {mine.isError && (
        <p role="alert" className="text-red-700">
          {t('common.loadError')}
        </p>
      )}
      {mine.data?.length === 0 && <p className="text-slate-600">{t('myclasses.empty')}</p>}
      <ul className="space-y-3">
        {mine.data?.map((c) => (
          <li key={c.sectionId} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">
                {c.className} – {c.sectionName}
              </h2>
              <span className="text-sm text-slate-600">{c.academicYear}</span>
            </div>
            <p className="mt-1 text-sm">
              {c.isClassTeacher && (
                <span className="mr-2 rounded bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-900">
                  {t('myclasses.classTeacher')}
                </span>
              )}
              {c.subjects.join(', ')}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {t('myclasses.students', { count: c.studentCount })}
            </p>
            <Link
              to={`/students?sectionId=${c.sectionId}`}
              className="mt-2 inline-block text-sm text-brand-700 underline"
            >
              {t('myclasses.viewStudents')}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
